import { useEffect } from "react";
import { Map, Route, Share2, Send, UserCircle2, ShieldCheck, Smartphone, Monitor } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { Reveal, MaskLine } from "@/components/site/Reveal";

const features = [
  { id: "discover", icon: Map, title: "Discover", text: "Browse an interactive map or searchable list of curated fly-in destinations — complete with amenities, fuel, accommodation, strip status reports, and photos — plus a national register of thousands of airfields, heliports, and major airports." },
  { id: "plan", icon: Route, title: "Plan your trip", text: "Build a trip by dropping stops on the map or searching airfields, starting from your Home Port. The app auto-pairs lodges to their nearest airstrip, suggests fuel stops and stopovers along the way, and draws your route with direction arrows and dashed connectors." },
  { id: "share", icon: Share2, title: "Save, share & export", text: "Save trips to your account, share them with friends via friend codes (no email needed), discuss them in group chat, and export as GPX/KML." },
  { id: "booking", icon: Send, title: "Booking enquiries", text: "Found a venue you like? Send a booking enquiry right from the app — specifying aircraft, dates, guests, rooms or camping sites, fuel, and pickup needs — and it emails the property on your behalf." },
  { id: "account", icon: UserCircle2, title: "Optional sign-in", text: "Sign-in is optional (email/password) and unlocks profiles, comments, strip-status reports, and the social features." },
  { id: "admin", icon: ShieldCheck, title: "Admin tools", text: "Admins get tools to curate listings, upload custom icons, bulk-import data, and run a soft launch per platform." },
];

const platforms = [
  { icon: Smartphone, label: "iPhone" },
  { icon: Smartphone, label: "Android" },
  { icon: Monitor, label: "Desktop" },
];

export default function AppPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Plan Land & Stay | Aero Soft Solutions";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="app-page">
      <header className="sky-wash relative overflow-hidden border-b border-sky-100">
        <div className="mx-auto max-w-6xl px-5 pb-20 pt-10 sm:px-6 lg:px-12">
          <Logo />
          <p className="mt-16 font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Fly-in travel companion · Australia</p>
          <h1 className="mt-5 font-display text-5xl font-semibold leading-[1.02] tracking-[-0.035em] text-ink sm:text-6xl lg:text-7xl" data-testid="app-title">
            <MaskLine delay={0.1}>Plan Land</MaskLine>
            <MaskLine delay={0.25}><span className="font-serif font-normal italic text-sky-600">&amp; Stay</span></MaskLine>
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg" data-testid="app-intro">
              Plan Land &amp; Stay is a fly-in travel companion for pilots that works on your phone (iPhone/Android) and desktop, helping you discover and plan trips to airstrips, pubs, and stay-overnight venues across Australia.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="app-platforms">
              {platforms.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/70 px-4 py-2 text-sm font-medium text-ink backdrop-blur">
                  <Icon className="h-4 w-4 text-sky-600" />{label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </header>

      <main className="bg-[#F4F9FD] py-20 lg:py-28">
        <div className="mx-auto grid max-w-6xl gap-5 px-5 sm:px-6 md:grid-cols-2 lg:grid-cols-3 lg:px-12">
          {features.map(({ id, icon: Icon, title, text }, i) => (
            <Reveal key={id} delay={(i % 3) * 0.08} className="h-full">
              <article className="flex h-full flex-col rounded-[28px] border border-sky-100 bg-white p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-sky-300 sm:p-8" data-testid={`app-feature-${id}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><Icon className="h-5 w-5" /></span>
                  <span className="font-mono text-xs tracking-[0.2em] text-slate-500">0{i + 1}</span>
                </div>
                <h2 className="mt-8 font-display text-2xl font-semibold tracking-tight text-ink">{title}</h2>
                <p className="mt-3 text-base leading-relaxed text-slate-600">{text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </main>

      <footer className="bg-[#071A2E] py-8 text-center text-xs text-slate-400">
        Plan Land &amp; Stay · © {new Date().getFullYear()} Aero Soft Solutions Pty Ltd · ABN 147 024 828 80
      </footer>
    </div>
  );
}
